import { useFormikContext, type FieldInputProps } from 'formik';
import React, { useCallback, useMemo } from 'react';

import {
  type RangeFilterValue,
  type FilterUnits,
  ReqsReviewConfigItemSelectOption,
  FILTER_QUERY_VALUE_TYPES,
  FilterQueryValueType,
  FILTER_UNITS,
  type NumericOperator,
  type StringOperator,
  REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES,
  ReqsReviewConfigItem,
  OPERATORS,
  STRING_OPERATORS,
  NULL_OPERATORS,
  FILTER_WIDGET_TYPES,
} from '@frontend/api';
import {
  Select,
  InputRightAddon,
  Input,
  SelectComponents,
  Flex,
  type CascaderSubmenu,
  HStack,
  Checkbox,
  VStack,
} from '@frontend/legacy-components';

import {
  SupplierFacilitySelector,
  ProcessSelector,
  AccountSelector,
} from 'modules/common/smartComponents';

import { type QueryWithId } from '../utils/queryBuilding';
import { isRangeValue } from '../utils/validation';
import { MultiStringInput } from './MultiStringInput';
import { NumberValueField } from './NumberValueField';
import { type FormStateData } from './RulesDetailModal';
import { SignalSelect, SignalSelectOptionType } from './SignalSelect';

const isNumberField = (
  value: QueryWithId['value'],
): value is number | RangeFilterValue =>
  typeof value === 'number' || isRangeValue(value);

const isAngleField = (valueType: FilterQueryValueType): valueType is 'angle' =>
  valueType === FILTER_QUERY_VALUE_TYPES.ANGLE;

const isDistanceField = (
  valueType: FilterQueryValueType,
): valueType is 'distance' | 'area' | 'volume' =>
  (
    [
      FILTER_QUERY_VALUE_TYPES.DISTANCE,
      FILTER_QUERY_VALUE_TYPES.AREA,
      FILTER_QUERY_VALUE_TYPES.VOLUME,
    ] as FilterQueryValueType[]
  ).includes(valueType);

const isRoughnessField = (
  valueType: FilterQueryValueType,
): valueType is 'roughness' => valueType === FILTER_QUERY_VALUE_TYPES.ROUGHNESS;

const roughnessSuffixForUnit = (unit: FilterUnits): string =>
  unit === FILTER_UNITS.IN ? 'µin' : 'µm';

type QueryFieldProps = {
  query: QueryWithId;
  isDisabled?: boolean;
  onChange: (value: QueryWithId) => void;
  signalIndex: number;
  groupIndex: number;
  queryIndex: number;
  cascaderOptions: CascaderSubmenu<SignalSelectOptionType>;
  onCascaderChange: (value: string[]) => void;
  cascaderValue: string[];
};

const NumberValueOnlyField = ({
  query,
  onChange,
  isDisabled,
  signalIndex,
  groupIndex,
  queryIndex,
}: Omit<
  QueryFieldProps,
  'cascaderOptions' | 'onCascaderChange' | 'cascaderValue'
>) => {
  return (
    <NumberValueField
      isDisabled={isDisabled}
      value={query.value as number | RangeFilterValue}
      min={0}
      onChange={(value) => onChange({ ...query, value })}
      signalIndex={signalIndex}
      groupIndex={groupIndex}
      queryIndex={queryIndex}
      valueType={query.valueType}
      className="numeric-query-number-field"
    />
  );
};

const DistanceValueOnlyField = ({
  query,
  onChange,
  isDisabled,
  unit, // new prop
  signalIndex,
  groupIndex,
  queryIndex,
}: Omit<
  QueryFieldProps,
  'cascaderOptions' | 'onCascaderChange' | 'cascaderValue'
> & {
  unit: FilterUnits;
}) => {
  const unitString = useMemo(() => {
    if (query.valueType === FILTER_QUERY_VALUE_TYPES.DISTANCE) {
      return unit;
    } else if (query.valueType === FILTER_QUERY_VALUE_TYPES.AREA) {
      return `sq. ${unit}`;
    } else if (query.valueType === FILTER_QUERY_VALUE_TYPES.VOLUME) {
      return `cu. ${unit}`;
    }
    return '';
  }, [unit, query.valueType]);
  return (
    <NumberValueField
      isDisabled={isDisabled}
      value={query.value as number | RangeFilterValue}
      min={0}
      onChange={(value) => onChange({ ...query, value, units: unit })}
      signalIndex={signalIndex}
      groupIndex={groupIndex}
      queryIndex={queryIndex}
      valueType={query.valueType}
      className="numeric-query-distance-field"
    >
      <InputRightAddon>{unitString}</InputRightAddon>
    </NumberValueField>
  );
};

const RoughnessValueOnlyField = ({
  query,
  onChange,
  isDisabled,
  unit,
  signalIndex,
  groupIndex,
  queryIndex,
}: Omit<
  QueryFieldProps,
  'cascaderOptions' | 'onCascaderChange' | 'cascaderValue'
> & {
  unit: FilterUnits;
}) => {
  return (
    <NumberValueField
      isDisabled={isDisabled}
      value={query.value as number | RangeFilterValue}
      min={0}
      onChange={(value) => onChange({ ...query, value, units: unit })}
      signalIndex={signalIndex}
      groupIndex={groupIndex}
      queryIndex={queryIndex}
      valueType={query.valueType}
      className="numeric-query-roughness-field"
    >
      <InputRightAddon>{roughnessSuffixForUnit(unit)}</InputRightAddon>
    </NumberValueField>
  );
};

const AngleValueOnlyField = ({
  query,
  onChange,
  isDisabled,
  signalIndex,
  groupIndex,
  queryIndex,
}: Omit<
  QueryFieldProps,
  'cascaderOptions' | 'onCascaderChange' | 'cascaderValue'
>) => {
  return (
    <NumberValueField
      isDisabled={isDisabled}
      value={query.value as number | RangeFilterValue}
      min={-360}
      max={360}
      onChange={(value) => onChange({ ...query, value })}
      signalIndex={signalIndex}
      groupIndex={groupIndex}
      queryIndex={queryIndex}
      valueType={query.valueType}
      className="numeric-query-angle-field"
    >
      <InputRightAddon>
        {query.value === 1 ? 'degree' : 'degrees'}
      </InputRightAddon>
    </NumberValueField>
  );
};

type NumericQueryFieldProps = Omit<QueryFieldProps, 'query'> & {
  query: NumericQuery;
  availableUnits: FilterUnits[];
};

type NumericQuery = Omit<QueryWithId, 'value' | 'operator'> & {
  value: number | RangeFilterValue;
  operator: NumericOperator;
};

const isNumericQuery = (query: QueryWithId): query is NumericQuery => {
  const isNumberOrRangeOrNull =
    typeof query.value === 'number' ||
    isRangeValue(query.value) ||
    query.value === null;

  // Check if value is a string that can be cast to a number (not NaN)
  const isStringNumber =
    typeof query.value === 'string' && !isNaN(Number(query.value));

  return (
    (isNumberOrRangeOrNull || isStringNumber) &&
    query.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.NUMERIC
  );
};

export const NumericQueryField = ({
  query,
  onChange,
  isDisabled,
  unit,
  signalIndex,
  groupIndex,
  queryIndex,
  cascaderOptions,
  onCascaderChange,
  cascaderValue,
}: NumericQueryFieldProps & { unit: FilterUnits }) => {
  if (!isNumberField(query.value)) {
    return null;
  }

  const valueComponent = isDistanceField(query.valueType) ? (
    <DistanceValueOnlyField
      query={query}
      onChange={onChange}
      isDisabled={isDisabled}
      unit={unit}
      signalIndex={signalIndex}
      groupIndex={groupIndex}
      queryIndex={queryIndex}
    />
  ) : isRoughnessField(query.valueType) ? (
    <RoughnessValueOnlyField
      query={query}
      onChange={onChange}
      isDisabled={isDisabled}
      unit={unit}
      signalIndex={signalIndex}
      groupIndex={groupIndex}
      queryIndex={queryIndex}
    />
  ) : isAngleField(query.valueType) ? (
    <AngleValueOnlyField
      query={query}
      onChange={onChange}
      isDisabled={isDisabled}
      signalIndex={signalIndex}
      groupIndex={groupIndex}
      queryIndex={queryIndex}
    />
  ) : (
    <NumberValueOnlyField
      query={query}
      onChange={onChange}
      isDisabled={isDisabled}
      signalIndex={signalIndex}
      groupIndex={groupIndex}
      queryIndex={queryIndex}
    />
  );

  return (
    <Flex gap={1} wrap="wrap" align="flex-start" flex={1}>
      <SignalSelect
        isDisabled={isDisabled}
        placeholder="Select field and operator"
        value={cascaderValue}
        options={cascaderOptions}
        onChange={onCascaderChange}
        isClearable={false}
        signalIndex={signalIndex}
        groupIndex={groupIndex}
        queryIndex={queryIndex}
      />
      {valueComponent}
    </Flex>
  );
};

const SelectFieldSelectContainer = (props: unknown) => (
  <SelectComponents.SelectContainer
    {...props}
    innerProps={{
      ...props.innerProps,
      width: 'auto',
      flexGrow: 1,
    }}
  />
);

type RulesSelectMenuListProps = React.ComponentProps<
  typeof SelectComponents.MenuList
>;
type RulesSelectOptionProps = React.ComponentProps<
  typeof SelectComponents.Option
>;

/** Dimension Role filter: wide menu + no wrap so labels like "Critical to quality" stay on one line. */
const DimensionRoleMenuList = (props: RulesSelectMenuListProps) => (
  <SelectComponents.MenuList
    {...props}
    innerProps={{
      ...props.innerProps,
      minW: 'max-content',
    }}
  />
);

const DimensionRoleOption = (props: RulesSelectOptionProps) => (
  <SelectComponents.Option
    {...props}
    innerProps={{
      ...props.innerProps,
      // Use style props, not `sx` — Option sets `sx` for focus/hover; `innerProps.sx` would
      // replace that prop and remove selection highlighting.
      whiteSpace: 'nowrap',
      wordBreak: 'normal',
      overflowWrap: 'anywhere',
    }}
  />
);

const SelectValueField = ({
  config,
  query,
  selectOptions,
  isDisabled,
  onChange,
  isArrayOperator,
  isNullOperator,
  fieldProps,
}: {
  config: ReqsReviewConfigItem;
  query: QueryWithId;
  selectOptions: ReqsReviewConfigItemSelectOption[];
  isDisabled?: boolean;
  onChange: (value: QueryWithId) => void;
  isArrayOperator: boolean;
  isNullOperator: boolean;
  fieldProps: FieldInputProps<QueryWithId['value']>;
}) => {
  const selectedOption = useMemo(() => {
    if (isArrayOperator) {
      if (Array.isArray(query.value)) {
        return query.value
          .map((v) => selectOptions.find((opt) => opt.value === v))
          .filter(Boolean);
      } else {
        return [
          selectOptions.find((option) => option.value === query.value),
        ].filter(Boolean);
      }
    } else {
      return (
        selectOptions.find((option) => option.value === query.value) ?? null
      );
    }
  }, [query.value, isArrayOperator, selectOptions]);

  const isDimensionRoleSelect =
    config.queryPath.length > 0 &&
    config.queryPath[config.queryPath.length - 1] === 'role';

  const selectFieldComponents = useMemo(
    () =>
      isDimensionRoleSelect
        ? {
            SelectContainer: SelectFieldSelectContainer,
            MenuList: DimensionRoleMenuList,
            Option: DimensionRoleOption,
          }
        : {
            SelectContainer: SelectFieldSelectContainer,
          },
    [isDimensionRoleSelect],
  );

  if (isNullOperator) {
    return null;
  }

  if (config.widget === FILTER_WIDGET_TYPES.ACCOUNT) {
    return (
      <AccountSelector
        value={isArrayOperator ? query.value : [query.value]}
        onChange={(values: Array<{ id: number }>) => {
          if (isArrayOperator) {
            if (values && values.length > 0) {
              const accountIds = values.map((v) => v.id);
              onChange({
                ...query,
                value: accountIds as number[] & QueryWithId['value'],
              });
            } else {
              onChange({
                ...query,
                value: [],
              });
            }
          } else {
            onChange({
              ...query,
              value: values[0]?.id ?? null,
            });
          }
        }}
        isDisabled={isDisabled}
        isMulti={isArrayOperator}
        styles={{
          container: (baseStyles: React.CSSProperties) => ({
            ...baseStyles,
            maxWidth: '250px',
          }),
          control: (baseStyles: React.CSSProperties) => ({
            ...baseStyles,
            minHeight: '0',
          }),
          valueContainer: (baseStyles: React.CSSProperties) => ({
            ...baseStyles,
            padding: '0',
          }),
        }}
      />
    );
  }

  if (config.widget === FILTER_WIDGET_TYPES.PROCESS) {
    return (
      <ProcessSelector
        selectedValues={
          Array.isArray(query.value)
            ? (query.value.filter((v) => typeof v === 'number') as number[])
            : typeof query.value === 'number'
            ? [query.value]
            : []
        }
        onChange={(selections: number[]) => {
          if (isArrayOperator) {
            onChange({
              ...query,
              value: selections as number[] & QueryWithId['value'],
            });
          } else {
            onChange({
              ...query,
              value: selections[0] ?? null,
            });
          }
        }}
        isDisabled={isDisabled}
        allowMultiSelect={isArrayOperator}
      />
    );
  }

  if (config.widget === FILTER_WIDGET_TYPES.FACILITY) {
    return (
      <SupplierFacilitySelector
        // Convert to string values on the way in
        value={
          Array.isArray(query.value)
            ? query.value
                .filter((v) => typeof v === 'number')
                .map((v) => v.toString())
            : typeof query.value === 'number'
            ? [query.value.toString()]
            : []
        }
        onChange={(values: string[] | string | null) => {
          // Convert to number values on the way out
          if (isArrayOperator && Array.isArray(values)) {
            const facilityIds = values.map((v) => parseInt(v, 10));
            if (facilityIds && facilityIds.length > 0) {
              onChange({
                ...query,
                value: facilityIds as number[] & QueryWithId['value'],
              });
            } else {
              onChange({
                ...query,
                value: [],
              });
            }
          } else if (!Array.isArray(values)) {
            const facilityId = parseInt(values ?? '-1', 10);
            onChange({
              ...query,
              value: facilityId !== -1 ? facilityId : null,
            });
          }
        }}
        isDisabled={isDisabled}
        allowMultiSelect={isArrayOperator}
      />
    );
  }

  return (
    <Select
      {...fieldProps}
      value={selectedOption}
      options={selectOptions}
      isClearable={false}
      isMulti={isArrayOperator}
      isDisabled={isDisabled}
      menuMatchReferenceWidth={!isDimensionRoleSelect}
      onChange={(
        option:
          | ReqsReviewConfigItemSelectOption
          | ReqsReviewConfigItemSelectOption[],
      ) => {
        if (isArrayOperator) {
          if (option === null) {
            onChange({ ...query, value: [] });
          } else {
            const options = Array.isArray(option) ? option : [option];
            onChange({
              ...query,
              value: options.map((opt) => opt.value),
            });
          }
        } else {
          const singleOption = Array.isArray(option) ? option[0] : option;
          onChange({ ...query, value: singleOption?.value ?? null });
        }
      }}
      components={selectFieldComponents}
    />
  );
};

export const SelectQueryField = ({
  config,
  query,
  selectOptions,
  isDisabled,
  onChange,
  signalIndex,
  groupIndex,
  queryIndex,
  cascaderOptions,
  onCascaderChange,
  cascaderValue,
}: QueryFieldProps & {
  config: ReqsReviewConfigItem;
  selectOptions: ReqsReviewConfigItemSelectOption[];
}) => {
  const { getFieldProps } = useFormikContext<FormStateData>();
  const fieldProps = useMemo(() => {
    return getFieldProps(
      `signals.${signalIndex}.groups.${groupIndex}.queries.${queryIndex}.value`,
    );
  }, [getFieldProps, signalIndex, groupIndex, queryIndex]);

  const isArrayOperator = [OPERATORS.IS_IN, OPERATORS.NOT_IN].includes(
    query.operator,
  );

  const isNullOperator = query.operator === NULL_OPERATORS.IS_NULL;

  return (
    <Flex gap={1} wrap="wrap" align="flex-start" flex={1}>
      <SignalSelect
        isDisabled={isDisabled}
        placeholder="Select field and operator"
        value={cascaderValue}
        options={cascaderOptions}
        onChange={onCascaderChange}
        isClearable={false}
        signalIndex={signalIndex}
        groupIndex={groupIndex}
        queryIndex={queryIndex}
      />
      <SelectValueField
        config={config}
        query={query}
        selectOptions={selectOptions}
        isDisabled={isDisabled}
        onChange={onChange}
        isArrayOperator={isArrayOperator}
        isNullOperator={isNullOperator}
        fieldProps={fieldProps}
      />
    </Flex>
  );
};

type StringQuery = Omit<QueryWithId, 'value' | 'operator'> & {
  value: string | string[];
  operator: StringOperator;
};

type StringQueryFieldProps = Omit<QueryFieldProps, 'query'> & {
  query: StringQuery;
};

const STRING_OPERATOR_OPTIONS: { label: string; value: StringOperator }[] = [
  {
    label: 'Includes',
    value: STRING_OPERATORS.INCLUDES_CASE_INSENSITIVE,
  },
  {
    label: 'Regex',
    value: STRING_OPERATORS.REGEX,
  },
];

export const StringQueryField = ({
  query,
  onChange,
  isDisabled,
  signalIndex,
  groupIndex,
  queryIndex,
}: StringQueryFieldProps) => {
  const { getFieldProps } = useFormikContext<FormStateData>();

  // Check if this is a multiselect string operator
  const isMultiSelectOperator =
    query.operator === STRING_OPERATORS.INCLUDES ||
    query.operator === STRING_OPERATORS.INCLUDES_CASE_INSENSITIVE;

  // Find the current operator option
  const currentOperatorOption = useMemo(() => {
    if (isMultiSelectOperator) {
      return STRING_OPERATOR_OPTIONS[0];
    } else {
      return STRING_OPERATOR_OPTIONS[1];
    }
  }, [isMultiSelectOperator]);

  const handleOperatorChange = useCallback(
    (option: { label: string; value: StringOperator }) => {
      onChange({
        ...query,
        operator: option.value,
        value:
          option.value === STRING_OPERATORS.INCLUDES_CASE_INSENSITIVE ? [] : '',
      });
    },
    [onChange, query],
  );

  const handleCaseSensitivityChange = useCallback(() => {
    onChange({
      ...query,
      operator:
        query.operator === STRING_OPERATORS.INCLUDES_CASE_INSENSITIVE
          ? STRING_OPERATORS.INCLUDES
          : STRING_OPERATORS.INCLUDES_CASE_INSENSITIVE,
    });
  }, [onChange, query]);

  const valueComponent = isMultiSelectOperator ? (
    <VStack spacing={0.5} align="stretch" flex={1}>
      <MultiStringInput
        value={Array.isArray(query.value) ? query.value : []}
        onChange={(values) => onChange({ ...query, value: values })}
        isDisabled={isDisabled}
        signalIndex={signalIndex}
        groupIndex={groupIndex}
        queryIndex={queryIndex}
      />
      <Checkbox
        isChecked={
          query.operator !== STRING_OPERATORS.INCLUDES_CASE_INSENSITIVE
        }
        onChange={handleCaseSensitivityChange}
        isDisabled={isDisabled}
        size="sm"
      >
        Match case
      </Checkbox>
    </VStack>
  ) : (
    <VStack spacing={0.5} align="flex-start" flex={1}>
      <Input
        {...getFieldProps(
          `signals.${signalIndex}.groups.${groupIndex}.queries.${queryIndex}.value`,
        )}
        value={typeof query.value === 'string' ? query.value : ''}
        onChange={(event) => onChange({ ...query, value: event.target.value })}
        autoComplete="off"
        isDisabled={isDisabled}
        width="100%"
        className="string-query-input"
      />
    </VStack>
  );

  return (
    <HStack
      spacing={1}
      flex={1}
      align="flex-start"
      className="string-query-input-container"
    >
      <Flex flexBasis={13}>
        <Select
          isDisabled={isDisabled}
          placeholder="Select operator"
          value={currentOperatorOption}
          options={STRING_OPERATOR_OPTIONS}
          onChange={handleOperatorChange}
          isClearable={false}
          className="string-query-operator-select"
        />
      </Flex>
      {valueComponent}
    </HStack>
  );
};

const isStringQuery = (query: QueryWithId): query is StringQuery =>
  (typeof query.value === 'string' ||
    query.value === null ||
    Array.isArray(query.value)) &&
  query.filterType === REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.STRING;

type BooleanQueryFieldProps = Omit<QueryFieldProps, 'query' | 'onChange'>;

const BooleanQueryField = ({
  isDisabled,
  signalIndex,
  groupIndex,
  queryIndex,
  cascaderOptions,
  onCascaderChange,
  cascaderValue,
}: BooleanQueryFieldProps) => {
  return (
    <Flex gap={1} wrap="wrap" align="flex-start" flex={1}>
      <SignalSelect
        isDisabled={isDisabled}
        placeholder="Select field and operator"
        value={cascaderValue}
        options={cascaderOptions}
        onChange={onCascaderChange}
        isClearable={false}
        signalIndex={signalIndex}
        groupIndex={groupIndex}
        queryIndex={queryIndex}
      />
    </Flex>
  );
};

type FilterQueryComponentProps = {
  signalIndex: number;
  groupIndex: number;
  queryIndex: number;
  onChange: (value: QueryWithId) => void;
  query: QueryWithId;
  config: ReqsReviewConfigItem;
  availableUnits: FilterUnits[];
  selectOptions: ReqsReviewConfigItemSelectOption[];
  isDisabled?: boolean;
  cascaderOptions: SignalSelectOptionType[];
  onCascaderChange: (value: string[]) => void;
  cascaderValue: string[];
  unit?: FilterUnits;
};

export const FilterQueryComponent = ({
  signalIndex,
  groupIndex,
  queryIndex,
  query,
  config,
  availableUnits,
  selectOptions,
  isDisabled,
  onChange,
  cascaderOptions,
  onCascaderChange,
  cascaderValue,
  unit,
}: FilterQueryComponentProps) => {
  switch (config.filterType) {
    case REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.NUMERIC:
      if (!isNumericQuery(query)) return null;
      return (
        <NumericQueryField
          query={query}
          availableUnits={availableUnits}
          onChange={onChange}
          isDisabled={isDisabled}
          signalIndex={signalIndex}
          groupIndex={groupIndex}
          queryIndex={queryIndex}
          cascaderOptions={cascaderOptions}
          onCascaderChange={onCascaderChange}
          cascaderValue={cascaderValue}
          unit={unit as FilterUnits}
        />
      );
    case REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.SELECT:
      return (
        <SelectQueryField
          config={config}
          query={query}
          selectOptions={selectOptions}
          onChange={onChange}
          isDisabled={isDisabled}
          signalIndex={signalIndex}
          groupIndex={groupIndex}
          queryIndex={queryIndex}
          cascaderOptions={cascaderOptions}
          onCascaderChange={onCascaderChange}
          cascaderValue={cascaderValue}
        />
      );
    case REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.STRING:
      if (!isStringQuery(query)) return null;
      return (
        <StringQueryField
          query={query}
          onChange={onChange}
          isDisabled={isDisabled}
          signalIndex={signalIndex}
          groupIndex={groupIndex}
          queryIndex={queryIndex}
          cascaderOptions={cascaderOptions}
          onCascaderChange={onCascaderChange}
          cascaderValue={cascaderValue}
        />
      );
    case REQS_REVIEW_CONFIG_ITEM_FILTER_TYPES.BOOLEAN:
      return (
        <BooleanQueryField
          isDisabled={isDisabled}
          signalIndex={signalIndex}
          groupIndex={groupIndex}
          queryIndex={queryIndex}
          cascaderOptions={cascaderOptions}
          onCascaderChange={onCascaderChange}
          cascaderValue={cascaderValue}
        />
      );
    default:
      return null;
  }
};
